import { RACK_COLORS } from "../../constants"
import { UTILITY_BENCH } from "../../modelPaths"
import type { SquatStandOptions } from "../../RackTypes"
import { findCustomOption, findBundleProduct, getAccessoryQuantity } from "../../utils/product-lookup"
import { getPlateSelection } from "../../utils/selection-utils"
import { getBarConfig } from "../bar/getBarConfig"
import { BenchBuilder } from "../equipment/benchBuilder"
import { SquatStandFactory } from "./squatStandFactory"
import type { IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { getSimpleForBuyRequest } from "@/js/helpers/buy-request"

// Config for the SML-1, SML-2, SML-2C, SML-3,
export function createSquatStandConfig(rogue: IRogue, buyRequest: BuyRequest) {
  const groupId = buyRequest.type === 'group' ? Number(buyRequest.productId) : Number(buyRequest.metadata?.groupedProductId)

  const groupPage = rogue.getProductById(groupId)
  const isSML2C = groupPage.name.includes('SML-2C')
  const isS4 = groupPage.sku.includes('S4SQUAT')
  
  let height: SquatStandOptions['height'] = '70'

  if (groupPage.name.includes('90"') || isSML2C) {
    height = '90'
  } else if (groupPage.name.includes('108"')) {
    height = '108'
  }

  let line: SquatStandOptions['line'] = 'SML'

  if (groupPage.name.includes('SM-')) {
    line = 'SM'
  }

  let pullup:SquatStandOptions['pullupBar']
  const pullupProduct = findCustomOption(rogue, buyRequest, 'pull-up') || findBundleProduct(rogue, buyRequest, 'pull-up')?.name

  if (pullupProduct?.toLowerCase().includes('fat')) {
    pullup = 'fatskinny'
  } else if (pullupProduct?.toLowerCase().includes('single')) {
    pullup = 'basic'
  }

  const bench = findBundleProduct(rogue, buyRequest, 'bench', true)
  const bar = findBundleProduct(rogue, buyRequest, 'add a bar', true)
  const plates = findBundleProduct(rogue, buyRequest, 'plates', true)

  const spotterArms = getAccessoryQuantity(rogue, buyRequest, 'spotter arms')

  const simple = getSimpleForBuyRequest(buyRequest, rogue)
  const color = RACK_COLORS[simple.attributes.rig_color as string || 'black']
  const numbered = simple.attributes.edition === 'Numbered'

  const config: SquatStandOptions = {
    line,
    height,
    color,
    spotterArms,
    pullupBar : pullup,
    bar       : bar ? getBarConfig(rogue, bar, plates) : undefined,
    bench     : BenchBuilder.getBenchPart(bench),
    numbered,
    hardware  : isSML2C ? 'isonite' : 'zinc',
    isSML2C,
    isS4,
  }

  return config
}

export function createSquatStand(rogue: IRogue, buyRequest: BuyRequest) {
  const config = createSquatStandConfig(rogue, buyRequest)
  const part = SquatStandFactory.build(config)

  return { config, part }
}

// Config for the HR-2
export function createHalfRackTwoConfig(rogue: IRogue, buyRequest: BuyRequest) {
  const largeHalfRack = findBundleProduct(rogue, buyRequest, 'height', true)?.name.includes('108-90')
  const safeties = getAccessoryQuantity(rogue, buyRequest, 'safety')
  const storage = getAccessoryQuantity(rogue, buyRequest, 'storage')

  const bar = findBundleProduct(rogue, buyRequest, 'add a bar', true)
  const bench = findBundleProduct(rogue, buyRequest, 'bench', true)

  const config: SquatStandOptions =   {
    line      : 'SML',
    numbered  : false,
    halfRack  : true,
    height    : '90',
    color     : 'black',
    pullupBar : 'basic',
    largeHalfRack,
    safeties,
    storage,
    bar       : bar ? getBarConfig(rogue, bar) : undefined,
    plates    : getPlateSelection(rogue, buyRequest),
    hardware  : 'zinc',
  }

  if (bench) {
    config.bench = { mesh: UTILITY_BENCH, slots: {} }
  }

  return config
}

export function createHalfRackTwo(rogue: IRogue, buyRequest: BuyRequest) {
  const config = createHalfRackTwoConfig(rogue, buyRequest)
  const part = SquatStandFactory.build(config)

  return { config, part }
}